import { useEffect, useMemo, useState } from "react"
import { Card, Button, Space, Select } from "antd"
import { PlusOutlined, DeleteOutlined, FilterOutlined } from "@ant-design/icons"
import DomainLv2Table from "./components/DomainLv2Table"
import DomainLv2Modal from "./components/DomainLv2Modal"
import { useCrud } from "@/hooks/useCrud"
import { DomainLv2, DomainLv2FormData } from "./types"
import { ID } from "@/types/common"
import domainLv2Api from "./api"
import { useDomainLv1 } from "@/hooks/useDomainLv1"

const { Option } = Select

const DomainLv2Management = () => {
  // 使用全局一级领域数据
  const { domainLv1s, isLoading: domainLv1sLoading } = useDomainLv1()
  //  选中的一级领域id
  const [selectedDomainLv1Id, setSelectedDomainLv1Id] = useState<
    ID | undefined
  >(undefined)

  // 使用 useMemo 根据选中的一级领域动态创建 api 对象
  const api = useMemo(
    () => ({
      getList: selectedDomainLv1Id
        ? () => domainLv2Api.getList(selectedDomainLv1Id)
        : () =>
            Promise.resolve({
              data: [],
              total: 0,
              page: 1,
              pageSize: 0,
              totalPages: 0,
            }),
      getById: domainLv2Api.getById,
      create: domainLv2Api.create,
      update: (id: ID, data: DomainLv2FormData | Partial<DomainLv2FormData>) =>
        domainLv2Api.update(id, data as DomainLv2FormData),
      delete: domainLv2Api.delete,
    }),
    [selectedDomainLv1Id]
  )

  const {
    items: domainLv2s,
    loading,
    opLoading,
    isModalOpen,
    editingItem: editingDomainLv2,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    loadItems,
  } = useCrud<DomainLv2, DomainLv2FormData>({
    api,
    entityName: "二级领域",
    autoLoad: false, // 禁用自动加载，等一级领域默认值设置后再加载
  })

  // 默认选中第一个一级领域
  useEffect(() => {
    if (domainLv1s.length > 0 && !selectedDomainLv1Id) {
      const firstDomainLv1Id = domainLv1s[0].id
      setSelectedDomainLv1Id(firstDomainLv1Id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainLv1s])

  // 当选中的一级领域变化时，加载对应的二级领域数据
  useEffect(() => {
    if (selectedDomainLv1Id && loadItems) {
      loadItems() // 使用最新的 api 对象加载数据
    }
  }, [selectedDomainLv1Id, loadItems])

  // 处理一级领域选择变化
  const handleDomainLv1Change = (domainLv1Id: ID) => {
    setSelectedDomainLv1Id(domainLv1Id)
    setSelectedIds([]) // 清空选中项
  }

  // 计算统计数据
  const totalDomainLv2s = domainLv2s.length
  const selectedCount = selectedIds.length
  const batchDisabled = selectedCount === 0
  // 按钮禁用：必须选中一级领域
  const addDisabled = !selectedDomainLv1Id

  return (
    <div className="p-6">
      {/* 主要内容区域 */}
      <Card>
        {/* 操作栏 */}
        <div className="flex justify-between items-center mb-4">
          <Space>
            <span className="text-gray-500">
              共 {totalDomainLv2s} 个二级领域
            </span>
            <div className="flex items-center gap-2">
              <FilterOutlined className="text-gray-500" />
              <span className="text-sm text-gray-600">一级领域：</span>
              <Select
                placeholder="请选择一级领域"
                style={{ width: 200 }}
                value={selectedDomainLv1Id}
                onChange={handleDomainLv1Change}
                loading={domainLv1sLoading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {domainLv1s.map((domainLv1) => (
                  <Option key={domainLv1.id} value={domainLv1.id}>
                    {domainLv1.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
              disabled={addDisabled}
              data-testid="btn-add-domain-lv2"
            >
              新增二级领域
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={batchDisabled}
              loading={opLoading}
              data-testid="btn-batch-delete"
            >
              批量删除 {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          </Space>
        </div>

        {/* 二级领域表格 */}
        <DomainLv2Table
          domainLv2s={domainLv2s}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Card>

      {/* 二级领域信息模态框 */}
      <DomainLv2Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        domainLv2={editingDomainLv2}
        loading={opLoading}
        initialValues={{
          domainLv1Id: selectedDomainLv1Id,
        }}
      />
    </div>
  )
}

export default DomainLv2Management
